/**
 * Public Privacy Policy and Terms of Service text for app.zenrentflow.com/privacy and /terms.
 * Fill every LEGAL_DETAILS value before production; the pages show a draft banner until then.
 * Keep the service-provider list in sync with what the backend actually calls.
 */

export const LEGAL_DETAILS = {
  operator: '[Legal business name]',
  contactEmail: 'privacy@zenrentflow.com',
  province: 'Alberta',
  effectiveDate: '[Effective date]'
};

export const isDraft = (details = LEGAL_DETAILS) =>
  Object.values(details).some((value) => /^\[.*\]$/.test(value));

export function privacyPolicy(d = LEGAL_DETAILS) {
  return {
    title: 'Privacy Policy',
    intro: `This policy explains what information ZenRentFlow collects, how we use it, and the choices you have. ZenRentFlow is property management software operated by ${d.operator} ("we", "us"). We follow Canada's Personal Information Protection and Electronic Documents Act (PIPEDA).`,
    sections: [
      {
        heading: 'Who is responsible for what',
        paragraphs: [
          'Landlords and property managers ("customers") use ZenRentFlow to run their rentals. For your own account details, we are responsible for your information.',
          'For information about tenants, applicants, prospects and contractors that a customer enters or collects through ZenRentFlow, the customer decides why it is collected and how it is used, and we handle it on their behalf. If you are a tenant or applicant, please contact your landlord first about your information. We will help them respond.'
        ]
      },
      {
        heading: 'Information we collect',
        bullets: [
          'Account details: name, email, phone number, company details and sign-in information.',
          'Rental records: properties, units, leases, rent schedules, payments, deposits, receipts, maintenance requests, inspections and photos, notices and messages.',
          'Rental applications: contact details, date of birth, employment and income, and, only if the applicant chooses to give them for a credit check, Social Insurance Number and driver’s licence number. Social Insurance Numbers are stored encrypted.',
          'Electronic signatures: the signature, signer name, time, IP address and browser details, so a signed document can be verified later.',
          'Receipts and bills: the files a customer uploads and the details read from them (vendor, date, amounts, tax).',
          'Showings: the name, contact details and chosen time of people who book a viewing.',
          'Connected accounts: when a customer connects Google Calendar or QuickBooks, the access tokens needed to work with that account.',
          'Technical information: server logs such as IP address, browser type and errors, used for security and troubleshooting. The app uses your browser’s storage to keep you signed in. It does not use advertising trackers.'
        ]
      },
      {
        heading: 'How we use information',
        bullets: [
          'To provide ZenRentFlow and the features customers choose to use.',
          'To send the emails and text messages customers trigger, such as notices, receipts, showing confirmations and reminders.',
          'To keep the service secure, prevent misuse and fix problems.',
          'To answer support requests and meet our legal obligations.'
        ],
        paragraphs: ['We do not sell personal information, and we do not use it for advertising.']
      },
      {
        heading: 'AI features',
        paragraphs: [
          'When a customer uses AI features, such as reading a receipt, writing a listing description or drafting a notice, the content needed for that task is sent to OpenAI to produce the result. OpenAI does not use data sent through its API to train its models unless the customer opts in. AI results are drafts: customers review and edit them before anything is saved or sent.'
        ]
      },
      {
        heading: 'Service providers we use',
        paragraphs: ['We share information only with providers that help us run ZenRentFlow, and only what they need for their part:'],
        bullets: [
          'Google Cloud and Firebase: hosting, database, file storage and sign-in.',
          'Railway: application servers.',
          'OpenAI: AI features described above.',
          'Stripe: payments for paid features, such as tenant screening.',
          'Resend and Google: sending email.',
          'Twilio: sending text messages.',
          'Tenant screening providers: only when a customer orders a screening and the applicant consents.',
          'Intuit QuickBooks and Google Calendar: only when a customer connects them.'
        ]
      },
      {
        heading: 'Where information is stored',
        paragraphs: [
          'Our database and files are stored with Google Cloud in the United States, and some providers above process information in the United States. Information stored outside Canada is subject to the laws of that country and may be accessed by its courts and authorities.'
        ]
      },
      {
        heading: 'How we protect information',
        paragraphs: [
          'Information is encrypted in transit. Sensitive identifiers such as Social Insurance Numbers are also encrypted when stored. Each customer can only see their own properties and records, and access by our team is limited to what is needed to run and support the service.'
        ]
      },
      {
        heading: 'How long we keep information',
        paragraphs: [
          'We keep information while the customer’s account is active. Financial records may need to be kept longer for tax purposes (the Canada Revenue Agency generally requires six years). When an account is closed, we delete or anonymize its information within a reasonable time, unless the law requires us to keep it.'
        ]
      },
      {
        heading: 'Your choices and rights',
        paragraphs: [
          `You can ask to see, correct or delete your personal information, or withdraw consent, subject to legal and contract limits. Customers can update most details in the app. For anything else, email ${d.contactEmail}. If you are not satisfied with our answer, you can contact the Office of the Privacy Commissioner of Canada.`
        ]
      },
      {
        heading: 'Children',
        paragraphs: ['ZenRentFlow is for adults managing rentals. It is not directed at children.']
      },
      {
        heading: 'Changes to this policy',
        paragraphs: [
          'We may update this policy. We will change the effective date above, and tell customers about important changes by email or in the app.'
        ]
      },
      {
        heading: 'Contact us',
        paragraphs: [`Questions about privacy: ${d.contactEmail}.`]
      }
    ]
  };
}

export function termsOfService(d = LEGAL_DETAILS) {
  return {
    title: 'Terms of Service',
    intro: `These terms are the agreement between you and ${d.operator} ("we", "us") for using ZenRentFlow, including its websites, apps and integrations. They also serve as the end-user licence agreement for the software. By creating an account or using ZenRentFlow, you agree to these terms.`,
    sections: [
      {
        heading: 'The service',
        paragraphs: [
          'ZenRentFlow helps landlords and property managers manage properties, tenants, leases, rent, expenses, maintenance and documents. We may add, change or remove features over time. Features marked as a preview may change more often.'
        ]
      },
      {
        heading: 'Your account',
        bullets: [
          'You must be at least 18 and able to enter into a contract, and you use ZenRentFlow for your rental business.',
          'Give accurate information and keep it up to date.',
          'Keep your sign-in details secure. You are responsible for activity in your account, including people you invite.'
        ]
      },
      {
        heading: 'Your data',
        paragraphs: [
          'You own the information you put into ZenRentFlow. You allow us to store and process it only to provide and improve the service, as described in our Privacy Policy.',
          'You are responsible for having the right to collect and use information about tenants, applicants and others, including any consent needed for credit or background checks, and for following the residential tenancy, privacy and human rights laws that apply to you.'
        ]
      },
      {
        heading: 'Acceptable use',
        paragraphs: ['You agree not to:'],
        bullets: [
          'break the law, or discriminate against, harass or mislead anyone, including through notices or messages sent from ZenRentFlow;',
          'send spam or messages people have not agreed to receive;',
          'access accounts or data that are not yours, or try to get around security or usage limits;',
          'copy, resell, reverse engineer or overload the service.'
        ]
      },
      {
        heading: 'AI drafts, notices and financial tools',
        paragraphs: [
          'AI features produce drafts that can contain mistakes. Review everything before you rely on it or send it.',
          'ZenRentFlow is not a law firm, accountant or financial institution and does not give legal, tax or accounting advice. Notices, leases and other documents must meet the rules in your province, and you are responsible for checking them.',
          'Rent, expense and accounting tools record and summarize information you provide. Exports and anything sent to accounting software such as QuickBooks should be reviewed by you or your accountant.'
        ]
      },
      {
        heading: 'Third-party services',
        paragraphs: [
          'Some features use other services, such as payment processing, tenant screening, email, text messaging, calendars and accounting software. Their own terms apply to your use of them, and we are not responsible for their services.'
        ]
      },
      {
        heading: 'Fees',
        paragraphs: [
          'If a feature costs money, the price is shown before you pay. Prices may include or add applicable taxes. Paid items such as screening reports are generally not refundable once the work has started, unless the law requires otherwise.'
        ]
      },
      {
        heading: 'Licence and ownership',
        paragraphs: [
          'We give you a limited, non-exclusive, non-transferable licence to use ZenRentFlow while your account is in good standing. We keep all rights in the software, designs and content we provide. If you send us feedback, we may use it without owing you anything.'
        ]
      },
      {
        heading: 'Availability',
        paragraphs: [
          'We work to keep ZenRentFlow available and your data safe, but we cannot promise the service will always be uninterrupted or error-free. Keep your own copies of records you are legally required to keep.'
        ]
      },
      {
        heading: 'Ending your use',
        paragraphs: [
          'You can stop using ZenRentFlow at any time. We may suspend or close an account that breaks these terms or puts others at risk. Before an account closes, you can export your data, except where we must act quickly to prevent harm.'
        ]
      },
      {
        heading: 'Disclaimers and limits on liability',
        paragraphs: [
          'To the extent the law allows, ZenRentFlow is provided "as is", without warranties of any kind.',
          'To the extent the law allows, we are not liable for indirect or consequential losses, such as lost rent, lost profits or lost data. Our total liability for any claim is limited to the greater of the amount you paid us in the 12 months before the claim and 100 Canadian dollars.',
          'You agree to cover our reasonable losses from claims caused by your data or by your use of ZenRentFlow in breach of these terms or the law.'
        ]
      },
      {
        heading: 'Governing law',
        paragraphs: [`These terms are governed by the laws of ${d.province} and the federal laws of Canada that apply there.`]
      },
      {
        heading: 'Changes to these terms',
        paragraphs: [
          'We may update these terms. We will change the effective date above and tell you about important changes in advance by email or in the app. Continuing to use ZenRentFlow after changes take effect means you accept them.'
        ]
      },
      {
        heading: 'Contact us',
        paragraphs: [`Questions about these terms: ${d.contactEmail}.`]
      }
    ]
  };
}
